import { Download, LoaderCircle } from "lucide-react";
import { useState } from "react";

import {
  downloadAgreementPreviewPdf,
  type CandidateAgreement,
  type TemplateVersion
} from "@/lib/api";

interface AgreementPreviewDownloadButtonProps {
  agreement: CandidateAgreement;
  candidate: {
    email: string;
    fullName: string;
  };
  templateVersion: TemplateVersion;
}

function openAgreementPrintFallback(title: string) {
  const previewRoot = window.document.querySelector<HTMLElement>("[data-agreement-preview-root='true']");

  if (!previewRoot) {
    return false;
  }

  const popup = window.open("", "_blank", "noopener,noreferrer");

  if (!popup) {
    return false;
  }

  const styleMarkup = Array.from(
    window.document.head.querySelectorAll("link[rel='stylesheet'], style")
  )
    .map((node) => node.outerHTML)
    .join("\n");

  popup.document.open();
  popup.document.write(`<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <base href="${window.location.origin}" />
    ${styleMarkup}
    <style>
      @page { margin: 14mm; }
      body { margin: 0; background: white; }
      .document-surface { box-shadow: none !important; border: 0 !important; }
    </style>
  </head>
  <body>
    ${previewRoot.outerHTML}
    <script>
      window.addEventListener("load", () => {
        window.print();
      });
    </script>
  </body>
</html>`);
  popup.document.close();

  return true;
}

export function AgreementPreviewDownloadButton({
  agreement,
  candidate,
  templateVersion
}: AgreementPreviewDownloadButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDownload() {
    setIsDownloading(true);
    setError(null);

    try {
      await downloadAgreementPreviewPdf({
        candidate,
        submission: agreement,
        template: {
          agreementBody: templateVersion.agreementBody,
          companyAddress: templateVersion.companyAddress,
          companyName: templateVersion.companyName,
          dataProtectionNotice: templateVersion.dataProtectionNotice,
          termsAndConditions: templateVersion.termsAndConditions,
          title: templateVersion.title,
          versionNumber: templateVersion.versionNumber
        }
      });
    } catch (requestError) {
      const fallbackOpened = openAgreementPrintFallback(templateVersion.title);

      if (fallbackOpened) {
        return;
      }

      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to open the agreement as PDF right now."
      );
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <button
        className="btn-secondary"
        disabled={isDownloading}
        onClick={() => void handleDownload()}
        type="button"
      >
        {isDownloading ? (
          <LoaderCircle className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        Download PDF
      </button>

      {error ? <p className="text-sm text-rose-700">{error}</p> : null}
    </div>
  );
}
